import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CheckClientNameResponse,
  Client,
  CreateClientInput,
  ListResponse,
  RenameClientInput,
} from "@salesboard/contracts";
import { useT } from "@/i18n";
import { api, toQueryString } from "@/lib/api";
import { reportMutationError, reportMutationSuccess } from "@/lib/mutations";

const KEY = "clients";

export function useClients(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: [KEY, params],
    queryFn: () => api.get<ListResponse<Client>>(`/clients${toQueryString(params)}`),
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: () => api.get<Client>(`/clients/${id}`),
    enabled: Boolean(id),
  });
}

/** One client by its URL slug, following a retired slug through history server-side. */
export function useClientBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: [KEY, "by-slug", slug],
    queryFn: () => api.get<Client>(`/clients/by-slug/${encodeURIComponent(slug!)}`),
    enabled: Boolean(slug),
  });
}

export function useSelectableClients(search: string) {
  return useQuery({
    queryKey: [KEY, "selectable", search],
    queryFn: () => api.get<{ id: string; name: string }[]>(`/clients/selectable${toQueryString({ q: search })}`),
  });
}

/** The duplicate check. A query, not a validation — it warns and never blocks. */
export function useCheckClientName() {
  return useMutation({
    mutationFn: (name: string) =>
      api.get<CheckClientNameResponse>(`/clients/check-name${toQueryString({ name })}`),
  });
}

export function useOpenOpportunityCount(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, id, "open-count"],
    queryFn: () => api.get<{ count: number }>(`/clients/${id}/open-opportunity-count`),
    enabled: Boolean(id),
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: (input: CreateClientInput) => api.post<Client>("/clients", input),
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(t("toast.created", client.name));
    },
    onError: (error) => reportMutationError(error),
  });
}

export function useRenameClient(id: string) {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: (input: RenameClientInput) => api.patch<Client>(`/clients/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(t("toast.clientRenamed"));
    },
    onError: (error) => reportMutationError(error),
  });
}

export function useSetClientActive(id: string) {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ active, version }: { active: boolean; version: number }) =>
      api.post<Client>(`/clients/${id}/${active ? "reactivate" : "deactivate"}`, { version }),
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(client.active ? t("toast.clientReactivated") : t("toast.clientDeactivated"));
    },
    onError: (error) => reportMutationError(error),
  });
}
